import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { customIndicatorApi, integrationApi } from '../../../services/sisApi';
import ComprehensiveAnalytics from './ComprehensiveAnalytics';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Analytics: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedIndicator, setSelectedIndicator] = useState('');
  const [academicYear, setAcademicYear] = useState('2024-2025');
  const [chartType, setChartType] = useState('bar');
  const [groupBy, setGroupBy] = useState('district');

  // Fetch indicators for selection
  const { data: indicatorsResponse } = useQuery({
    queryKey: ['customIndicators'],
    queryFn: () => customIndicatorApi.getAll({ is_active: true }),
  });

  // Fetch chart data
  const { data: chartData, isLoading: chartLoading } = useQuery({
    queryKey: ['indicatorChart', selectedIndicator, academicYear, chartType, groupBy],
    queryFn: () => integrationApi.getIndicatorChart(Number(selectedIndicator), {
      academic_year: academicYear,
      chart_type: chartType as any,
      group_by: groupBy as any,
    }),
    enabled: !!selectedIndicator,
  });

  const indicators = indicatorsResponse?.data?.results || [];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const sampleTrendData = [
    { year: '2020-2021', value: 450 },
    { year: '2021-2022', value: 478 },
    { year: '2022-2023', value: 502 },
    { year: '2023-2024', value: 534 },
    { year: '2024-2025', value: 567 },
  ];

  const sampleComparisonData = [
    { name: 'Unguja Urban', students: 12450, teachers: 456, ratio: 27.3 },
    { name: 'Unguja South', students: 8920, teachers: 334, ratio: 26.7 },
    { name: 'Pemba North', students: 6780, teachers: 287, ratio: 23.6 },
    { name: 'Pemba South', students: 5640, teachers: 245, ratio: 23.0 },
  ];

  const samplePieData = [
    { name: 'Primary', value: 65, count: 1003 },
    { name: 'Secondary', value: 25, count: 386 },
    { name: 'Pre-Primary', value: 10, count: 154 },
  ];

  const renderChart = () => {
    if (!selectedIndicator || chartLoading) {
      return (
        <Alert severity="info">
          {!selectedIndicator 
            ? 'Select an indicator to view analytics' 
            : 'Loading chart data...'
          }
        </Alert>
      );
    }

    const data = chartData?.data?.data || sampleComparisonData;

    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={samplePieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {samplePieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );
      
      default: // bar chart
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="students" fill="#8884d8" name="Students" />
              <Bar dataKey="teachers" fill="#82ca9d" name="Teachers" />
            </BarChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        SIS Analytics & Visualization
      </Typography>

      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="analytics tabs">
          <Tab label="All Indicators" />
          <Tab label="Custom Analytics" />
          <Tab label="Trend Analysis" />
          <Tab label="Comparison Dashboard" />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        {/* All Indicators - Comprehensive Analytics */}
        <ComprehensiveAnalytics />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Custom Indicator Analytics */}
        <Grid container spacing={3}>
          {/* Controls */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Chart Configuration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Indicator</InputLabel>
                      <Select
                        value={selectedIndicator}
                        label="Indicator"
                        onChange={(e) => setSelectedIndicator(e.target.value)}
                      >
                        {indicators.map((indicator: any) => (
                          <MenuItem key={indicator.id} value={indicator.id}>
                            {indicator.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      select
                      label="Academic Year"
                      value={academicYear}
                      onChange={(e) => setAcademicYear(e.target.value)}
                    >
                      <MenuItem value="2023-2024">2023-2024</MenuItem>
                      <MenuItem value="2024-2025">2024-2025</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      select
                      label="Chart Type"
                      value={chartType}
                      onChange={(e) => setChartType(e.target.value)}
                    >
                      <MenuItem value="bar">Bar Chart</MenuItem>
                      <MenuItem value="line">Line Chart</MenuItem>
                      <MenuItem value="pie">Pie Chart</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      select
                      label="Group By"
                      value={groupBy}
                      onChange={(e) => setGroupBy(e.target.value)}
                    >
                      <MenuItem value="district">District</MenuItem>
                      <MenuItem value="region">Region</MenuItem>
                      <MenuItem value="school">School</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Chart */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {selectedIndicator ? 
                    indicators.find((i: any) => i.id === Number(selectedIndicator))?.name || 'Indicator Chart'
                    : 'Select an Indicator'
                  }
                </Typography>
                {renderChart()}
              </CardContent>
            </Card>
          </Grid>

          {/* Summary Stats */}
          {selectedIndicator && (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Total Schools
                      </Typography>
                      <Typography variant="h4">
                        1,547
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Average Value
                      </Typography>
                      <Typography variant="h4">
                        24.2
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Highest Value
                      </Typography>
                      <Typography variant="h4">
                        45.8
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Data Coverage
                      </Typography>
                      <Typography variant="h4">
                        89%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {/* Trend Analysis */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Enrollment Trends Over Time
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={sampleTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#8884d8" 
                      strokeWidth={3}
                      name="Total Students"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Growth Rate Analysis
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Year-over-year growth rate
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    +6.2%
                  </Typography>
                </Box>
                <Alert severity="success">
                  Consistent growth in student enrollment over the past 5 years
                </Alert>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Forecasted Enrollment
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Projected for 2025-2026
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    602
                  </Typography>
                </Box>
                <Alert severity="info">
                  Based on current growth trends and demographic data
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {/* Comparison Dashboard */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  District Comparison - Student-Teacher Ratio
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={sampleComparisonData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="ratio" fill="#8884d8" name="Student-Teacher Ratio" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  School Level Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={samplePieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {samplePieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Key Insights
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Chip label="Best Ratio: Pemba South (23.0)" color="success" size="small" />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Chip label="Needs Attention: Unguja Urban" color="warning" size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Average ratio across all districts is 25.1 students per teacher
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Performance Indicators Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" color="primary.main">1,547</Typography>
                      <Typography variant="body2" color="text.secondary">Total Schools</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" color="success.main">828K</Typography>
                      <Typography variant="body2" color="text.secondary">Total Students</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" color="warning.main">15.2K</Typography>
                      <Typography variant="body2" color="text.secondary">Total Staff</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" color="error.main">25.1</Typography>
                      <Typography variant="body2" color="text.secondary">Avg. Ratio</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default Analytics;